/**
 * iter334cf — Super Admin BYOK integrations panel.
 *
 * Rendered inside SuperAdminSettings.jsx. Configures:
 *   • BYOK Stripe (secret + publishable + webhook secret) — used to
 *     charge tenants for their monthly SaaS subscription.
 *   • BYOK Resend (api key + sender email + name) — used to send
 *     SaaS invoices / past-due notices / welcome emails from the
 *     platform to tenant admins.
 *   • Grace period + suspension window before feature downgrade on
 *     failed SaaS billing.
 *
 * Never round-trips the raw secret to the FE: reads return masked
 * prefixes; writes send the raw values one direction only.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, KeyRound, Mail, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

const API = (typeof window !== "undefined" && window.location
  ? window.location.origin
  : process.env.REACT_APP_BACKEND_URL) + "/api";

const authHeader = () => {
  const token = localStorage.getItem("token") || localStorage.getItem("access_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const StatusIcon = ({ ok }) =>
  ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" />
     : <XCircle className="h-4 w-4 text-slate-400" />;

export default function SuperAdminByokPanel() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null); // 'stripe' | 'resend' | null

  // Local form state — only sent to backend on Save. Empty means "no
  // change" (backend leaves the stored value alone).
  const [stripeSecret, setStripeSecret] = useState("");
  const [stripePub, setStripePub] = useState("");
  const [stripeWebhook, setStripeWebhook] = useState("");
  const [resendKey, setResendKey] = useState("");
  const [senderEmail, setSenderEmail] = useState("");
  const [senderName, setSenderName] = useState("");
  const [alertEmail, setAlertEmail] = useState("");
  const [graceDays, setGraceDays] = useState(7);
  const [suspensionDays, setSuspensionDays] = useState(3);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/super-admin/settings`, { headers: authHeader() });
      setState(data);
      setStripePub(data.stripe_publishable_key || "");
      setSenderEmail(data.billing_sender_email || "");
      setSenderName(data.billing_sender_name || "");
      setAlertEmail(data.alert_email || "");
      setGraceDays(data.grace_period_days ?? 7);
      setSuspensionDays(data.suspension_days_after_grace ?? 3);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load platform settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        stripe_publishable_key: stripePub || null,
        billing_sender_email: senderEmail || null,
        billing_sender_name: senderName || null,
        alert_email: alertEmail || null,
        grace_period_days: Number(graceDays),
        suspension_days_after_grace: Number(suspensionDays),
      };
      if (stripeSecret.trim()) body.stripe_secret_key = stripeSecret.trim();
      if (stripeWebhook.trim()) body.stripe_webhook_secret = stripeWebhook.trim();
      if (resendKey.trim()) body.resend_api_key = resendKey.trim();
      const { data } = await axios.put(`${API}/super-admin/settings`, body, { headers: authHeader() });
      setState(data);
      setStripeSecret("");
      setStripeWebhook("");
      setResendKey("");
      toast.success("Platform settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const testStripe = async () => {
    setTesting("stripe");
    try {
      const { data } = await axios.post(`${API}/super-admin/settings/stripe/test`, {}, { headers: authHeader() });
      toast.success(`Stripe OK · ${data.account_id} (${data.livemode ? "LIVE" : "TEST"})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Stripe test failed");
    } finally { setTesting(null); }
  };

  const testResend = async () => {
    setTesting("resend");
    try {
      await axios.post(`${API}/super-admin/settings/resend/test`, {}, { headers: authHeader() });
      toast.success("Resend OK");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resend test failed");
    } finally { setTesting(null); }
  };

  const testSendResend = async () => {
    const to = window.prompt("Send a test email to which address?", senderEmail || "");
    if (!to) return;
    setTesting("resend-send");
    try {
      const { data } = await axios.post(
        `${API}/super-admin/settings/resend/test-send`,
        { to_email: to.trim() },
        { headers: authHeader() },
      );
      const r = data?.result || {};
      if (r.ok) {
        toast.success(`Test email sent from ${data.from_email} → ${data.to_email}. Check the inbox + Resend dashboard.`);
      } else {
        const hint = String(r.reason || "").startsWith("resend_4")
          ? " — Resend rejected it. Most likely the sender domain is not verified in this Resend account."
          : "";
        toast.error(`Not sent (${r.reason || "unknown"}) from ${data.from_email}.${hint}`, { duration: 12000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test send failed");
    } finally { setTesting(null); }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-8">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading BYOK settings…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="super-admin-byok-panel">
      {/* ---------- Stripe BYOK ---------- */}
      <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-blue-600" />
            <h3 className="font-semibold text-slate-900">Stripe BYOK — SaaS Billing</h3>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <StatusIcon ok={state?.stripe_configured} />
            <span className="text-slate-600">{state?.stripe_configured ? state.stripe_key_prefix : "Not configured"}</span>
          </div>
        </div>
        <p className="text-xs text-slate-500">
          Used by the platform to charge tenants for their monthly plans. Independent from each tenant&apos;s own Stripe key used for parent payments.
        </p>
        <div className="grid grid-cols-1 gap-3">
          <label className="text-xs">
            <span className="block text-slate-700 mb-1">Secret Key (sk_live_… or sk_test_…)</span>
            <input type="password" className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm font-mono"
                   placeholder={state?.stripe_configured ? "•••••• (leave blank to keep existing)" : "sk_..."}
                   value={stripeSecret}
                   onChange={(e) => setStripeSecret(e.target.value)}
                   data-testid="super-admin-stripe-secret" />
          </label>
          <label className="text-xs">
            <span className="block text-slate-700 mb-1">Publishable Key (pk_...)</span>
            <input type="text" className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm font-mono"
                   placeholder="pk_live_..."
                   value={stripePub}
                   onChange={(e) => setStripePub(e.target.value)}
                   data-testid="super-admin-stripe-publishable" />
          </label>
          <label className="text-xs">
            <span className="block text-slate-700 mb-1">Webhook Secret (whsec_...)</span>
            <input type="password" className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm font-mono"
                   placeholder={state?.stripe_webhook_secret_configured ? "•••••• (leave blank to keep existing)" : "whsec_..."}
                   value={stripeWebhook}
                   onChange={(e) => setStripeWebhook(e.target.value)}
                   data-testid="super-admin-stripe-webhook" />
          </label>
        </div>
        <button onClick={testStripe}
                disabled={!state?.stripe_configured || testing === "stripe"}
                className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1"
                data-testid="super-admin-stripe-test">
          {testing === "stripe" && <Loader2 className="h-3 w-3 animate-spin" />}
          Test Stripe connection
        </button>
      </div>

      {/* ---------- Resend BYOK ---------- */}
      <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-purple-600" />
            <h3 className="font-semibold text-slate-900">Resend BYOK — Platform Emails</h3>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <StatusIcon ok={state?.resend_configured} />
            <span className="text-slate-600">{state?.resend_configured ? state.resend_key_prefix : "Not configured"}</span>
          </div>
        </div>
        <p className="text-xs text-slate-500">
          Used ONLY for platform → tenant admin emails (SaaS invoices, welcome, past-due, suspension). Tenant-facing parent emails use each tenant&apos;s own configured sender.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-xs">
            <span className="block text-slate-700 mb-1">API Key (re_...)</span>
            <input type="password" className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm font-mono"
                   placeholder={state?.resend_configured ? "•••••• (leave blank to keep existing)" : "re_..."}
                   value={resendKey}
                   onChange={(e) => setResendKey(e.target.value)}
                   data-testid="super-admin-resend-key" />
          </label>
          <label className="text-xs">
            <span className="block text-slate-700 mb-1">Sender Email</span>
            <input type="email" className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                   placeholder="billing@yourplatform.com"
                   value={senderEmail}
                   onChange={(e) => setSenderEmail(e.target.value)}
                   data-testid="super-admin-sender-email" />
          </label>
          <label className="text-xs sm:col-span-2">
            <span className="block text-slate-700 mb-1">Sender Name</span>
            <input type="text" className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                   placeholder="Your Platform Billing"
                   value={senderName}
                   onChange={(e) => setSenderName(e.target.value)}
                   data-testid="super-admin-sender-name" />
          </label>
          <label className="text-xs sm:col-span-2">
            <span className="block text-slate-700 mb-1">
              Alert Email <span className="text-slate-400">(data-issue &amp; billing alerts)</span>
            </span>
            <input type="email" className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
                   placeholder="alerts@yourplatform.com"
                   value={alertEmail}
                   onChange={(e) => setAlertEmail(e.target.value)}
                   data-testid="super-admin-alert-email" />
            <span className="block text-slate-400 mt-1">
              Receives the nightly data-drift digest and billing alerts across all tenants.
            </span>
          </label>
        </div>
        <button onClick={testResend}
                disabled={!state?.resend_configured || testing === "resend"}
                className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1"
                data-testid="super-admin-resend-test">
          {testing === "resend" && <Loader2 className="h-3 w-3 animate-spin" />}
          Test Resend connection
        </button>
        <button onClick={testSendResend}
                disabled={!state?.resend_configured || testing === "resend-send"}
                className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-1 ml-2"
                data-testid="super-admin-resend-test-send">
          {testing === "resend-send" && <Loader2 className="h-3 w-3 animate-spin" />}
          Send test email
        </button>
        <p className="text-[11px] text-slate-400 mt-1">
          &quot;Test connection&quot; only checks the key. &quot;Send test email&quot; actually delivers
          one from the sender above — use it to confirm the sending domain is verified in Resend.
        </p>
      </div>

      {/* ---------- Grace + Suspension ---------- */}
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-5 space-y-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-700" />
          <h3 className="font-semibold text-amber-900">Failed Billing → Suspension Timeline</h3>
        </div>
        <p className="text-xs text-amber-800">
          After a failed SaaS charge, the tenant enters a <strong>grace period</strong>. If still unpaid after grace + suspension window, their feature set downgrades to core-only until payment resumes.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs">
            <span className="block text-amber-900 mb-1">Grace period (days after failed charge)</span>
            <input type="number" min="0" className="w-full rounded border border-amber-300 px-2 py-1.5 text-sm"
                   value={graceDays}
                   onChange={(e) => setGraceDays(e.target.value)}
                   data-testid="super-admin-grace-days" />
          </label>
          <label className="text-xs">
            <span className="block text-amber-900 mb-1">Additional days before suspension</span>
            <input type="number" min="0" className="w-full rounded border border-amber-300 px-2 py-1.5 text-sm"
                   value={suspensionDays}
                   onChange={(e) => setSuspensionDays(e.target.value)}
                   data-testid="super-admin-suspension-days" />
          </label>
        </div>
      </div>

      {/* ---------- Save ---------- */}
      <div className="flex justify-end">
        <button onClick={save}
                disabled={saving}
                className="px-4 py-2 rounded bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                data-testid="super-admin-byok-save">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          Save Settings
        </button>
      </div>
    </div>
  );
}
